import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drives a paginated / infinite-scroll list against any API function that resolves to
 * the standard envelope `{ data: [...], pagination: { page, limit, total, totalPages } }`.
 *
 * Usage:
 *   const list = usePaginatedList(salesApi.listSales, { limit: 10, params: { status } });
 *   ...map list.items...  <SentinelOn list.hasMore onReach={list.loadMore} />
 *
 * - Refetches from page 1 whenever `params` changes (deep-compared by JSON).
 * - `loadMore()` appends the next page; ignored while a fetch is in flight or exhausted.
 * - Exposes `loading` (first page), `loadingMore`, `empty`, `error`, `refresh`.
 */
export function usePaginatedList(fetcher, { limit = 20, params = {}, enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const paramsKey = JSON.stringify(params);
  const inFlight = useRef(false);
  const pageRef = useRef(1);
  // Race guard: every fetch gets a generation number and aborts the previous
  // request. A page-1 fetch (filter/tab change) always supersedes whatever is in
  // flight — without this, switching tabs mid-request silently kept the OLD tab's
  // results (the new fetch was dropped by the in-flight guard, and the stale
  // response landed last).
  const genRef = useRef(0);
  const abortRef = useRef(null);

  const fetchPage = useCallback(
    async (page, replace) => {
      if (!enabled) return;
      if (!replace && inFlight.current) return; // loadMore never stacks
      const gen = ++genRef.current;
      abortRef.current?.abort();
      const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
      abortRef.current = ac;
      inFlight.current = true;
      if (replace) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        // Fetchers that accept a config forward the abort signal; ones that don't
        // are still protected by the generation check below.
        const res = await fetcher({ ...JSON.parse(paramsKey), page, limit }, ac ? { signal: ac.signal } : undefined);
        if (gen !== genRef.current) return; // superseded — drop stale response
        const rows = res?.data ?? res?.items ?? [];
        setPagination(res?.pagination ?? null);
        pageRef.current = page;
        setItems((prev) => (replace ? rows : [...prev, ...rows]));
      } catch (err) {
        if (gen !== genRef.current) return; // aborted/superseded — not an error
        setError(err);
      } finally {
        if (gen === genRef.current) {
          inFlight.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [fetcher, paramsKey, limit, enabled]
  );

  // Abort any in-flight request on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  // reset + load page 1 when params change
  useEffect(() => {
    if (!enabled) return;
    setItems([]);
    pageRef.current = 1;
    fetchPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, enabled]);

  const hasMore = pagination ? pageRef.current < pagination.totalPages : false;

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchPage(pageRef.current + 1, false);
  }, [loading, loadingMore, hasMore, fetchPage]);

  const refresh = useCallback(() => fetchPage(1, true), [fetchPage]);

  return {
    items,
    pagination,
    loading,
    loadingMore,
    error,
    hasMore,
    empty: !loading && items.length === 0,
    loadMore,
    refresh,
  };
}
